import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import DaysTalkBoardV4 from './DaysTalkBoardV4';
import ContentCardV4 from '../../../common/component/ContentCardV4';
import ContentCardWrapperV4 from '../../../common/component/ContentCardWrapperV4';
import FetchResultWrapper from '../../../common/component/FetchResultWrapper';
import {REGION} from '../../../common/constant';
import {Page} from '../../../common/constant/page';
import useTypedSelector from '../../../common/hook/useTypedSelector';
import {useFetchInfo} from '../../../common/util/fetch';
import {routeTo} from '../../../common/util/PageRouter';
import {IBigContent} from '../../../commonSub/type';
import {getContentOrdNum} from '../../common/common';
import HomeFooter from '../../common/component/HomeFooter';
import {sendHomeEvent, sendHomeViewImp} from '../../common/tracking';
import FilterButtonV4 from '../component/FilterButtonV4';
import TimeTableBigContentCardV4 from '../component/TimeTableBigContentCardV4';
import {getTimeTableSortingOption} from '../function';
import {actions, ActionType} from '../state/action';
import {
  ITimeTableContent,
  ITimeTableDay,
  ITimeTablePage,
  ITimeTableSortKey,
} from '../type';

interface IProps {
  page: ITimeTablePage;
  currentDayTab: ITimeTableDay;
}

export default function DaysContentTableV4({page, currentDayTab}: IProps) {
  const isKorea = REGION === 'kor';
  const daysTab = useTypedSelector(state => state.timeTable[page].daysTab);
  const {isLoading} = useFetchInfo(ActionType.FetchWeekday, currentDayTab);
  const daysItem = daysTab[currentDayTab];

  const {t} = useTranslation();
  const dispatch = useDispatch();
  const sortKey = useTypedSelector(state => state.timeTable.sortKeyByDayTab);

  const handleClickContent = ({
    content,
    index,
    isAnchor,
    placement,
  }: {
    content: ITimeTableContent | IBigContent;
    index: number;
    isAnchor: boolean;
    placement?: string;
  }) => {
    const {id, sid} = content;
    routeTo({page: Page.Content, id, sid});
    sendHomeEvent({
      action: 'click',
      actionName: 'content',
      customProps: {
        content_id: id,
        card_id: content.cardId,
        event_layer: 'section',
        ordnum: index,
        list_type: isAnchor ? undefined : 'triple',
        section_id: placement,
      },
    });
  };

  const handleClickFilterButton = () => {
    sendHomeEvent({
      action: 'click',
      actionName: 'sorting_option',
      customProps: {
        event_layer: 'bottom',
      },
    });
  };

  const onSendViewImpFilterModal = useCallback(() => {
    sendHomeViewImp({
      customProps: {
        event_layer: 'popup',
        popup_type: 'timetable_sorting_option',
      },
    });
  }, []);

  const handleClickCloseModal = (isDimmedClicked?: boolean) => {
    sendHomeEvent({
      action: 'click',
      actionName: 'popup_close',
      customProps: {
        event_layer: typeof isDimmedClicked === 'boolean' ? 'dimmed' : 'popup',
        popup_type: 'timetable_sorting_option',
      },
    });
  };

  const handleClickConfirm = (key: ITimeTableSortKey) => {
    dispatch(actions.fetchWeekday({key, day: currentDayTab}));
    sendHomeEvent({
      action: 'click',
      actionName: 'popup_ok',
      customProps: {
        event_layer: 'popup',
        popup_type: 'timetable_sorting_option',
        sorting_option: getTimeTableSortingOption(key),
      },
    });
  };

  const {bigContent, contents, talkBoard, placement} = daysItem;
  const isEmpty = !bigContent && contents.length === 0;

  return (
    <>
      <FetchResultWrapper
        showLoading={!daysItem || isLoading}
        showNoItems={isEmpty}
        noItemsProps={{textKey: 'schedule_empty'}}
      >
        <>
          <div className="flex flex-col gap-4">
            {bigContent && (
              <TimeTableBigContentCardV4
                index={0}
                content={bigContent}
                onClick={() =>
                  handleClickContent({
                    content: bigContent,
                    index: 1,
                    isAnchor: true,
                    placement,
                  })
                }
              />
            )}
            <ContentCardWrapperV4 showEmptyContentCard>
              {contents.map((content, index) => (
                <ContentCardV4
                  key={content.id}
                  content={content}
                  onClick={() =>
                    handleClickContent({
                      content,
                      index: getContentOrdNum(index, !!bigContent),
                      isAnchor: false,
                      placement,
                    })
                  }
                />
              ))}
            </ContentCardWrapperV4>
            <DaysTalkBoardV4 talkBoard={talkBoard} />
          </div>
          {isKorea && (
            <FilterButtonV4
              itemList={TIME_TABLE_SORT_LIST}
              selectedItemKey={sortKey}
              bottomButtonIconVariant="sort-up"
              title={t('schedule_sort_title')}
              needTextTranslation={true}
              onSendViewImp={onSendViewImpFilterModal}
              onClick={handleClickFilterButton}
              onClickConfirm={handleClickConfirm}
              onClickCloseModal={handleClickCloseModal}
            />
          )}
        </>
      </FetchResultWrapper>
      <HomeFooter isBottomFixed={isEmpty} />
    </>
  );
}

const TIME_TABLE_SORT_LIST: Array<{key: ITimeTableSortKey; text: string}> = [
  {key: 'recommend', text: 'schedule_sort_recommend'},
  {key: 'freePublishing', text: 'schedule_sort_early_access'},
  {key: 'waitForFree', text: 'schedule_sort_gidamu'},
];
